import dayjs from '../../../utils/dayjsConfig'
import { effectiveLocationStr } from '../../../helpers/utils'
import { TASK_TYPES_KEYS } from '../../../pos/constants/constants'

const DETAIL_PRIORITY = {
  check_out: 0,
  activity: 1,
  check_in: 2
}

const normalizeDate = (value) => {
  if (!value) return null

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

const normalizeDateTime = (value) => {
  if (!value) return null

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.toISOString() : null
}

const buildVisitDateTime = (dateValue, timeValue) => {
  const normalizedDate = normalizeDate(dateValue)
  if (!normalizedDate || !timeValue || timeValue === '-') return null

  const normalizedTime = String(timeValue).trim()
  const parsed = dayjs(
    `${normalizedDate} ${normalizedTime}`,
    [
      'YYYY-MM-DD HH:mm:ss A',
      'YYYY-MM-DD h:mm:ss A',
      'YYYY-MM-DD HH:mm A',
      'YYYY-MM-DD h:mm A',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD H:mm:ss',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD H:mm'
    ],
    true
  )

  if (parsed.isValid()) return parsed.toISOString()

  const fallbackParsed = dayjs(`${normalizedDate}T${normalizedTime}`)
  return fallbackParsed.isValid() ? fallbackParsed.toISOString() : null
}

export const buildActivityDetailUrl = (activityName, activityId) => {
  if (!activityName || !activityId) return ''

  const normalizedActivityName = String(activityName).toLowerCase()
  let url = ''

  switch (true) {
    case normalizedActivityName.includes('cotiz'):
      url = `/cotizaciones/${activityId}`
      break
    case normalizedActivityName.includes('pedido'):
      url = `/pedidos/${activityId}`
      break
    case normalizedActivityName.includes('devoluci'):
    case normalizedActivityName.includes('rechazo'):
      url = `/devoluciones/${activityId}`
      break
    case normalizedActivityName.includes('no efectiva'):
      url = `/visitas-no-efectivas/${activityId}`
      break
    case normalizedActivityName.includes('entrega'):
      url = `/entregas/${activityId}`
      break
    case normalizedActivityName.includes('cobro'):
      url = `/cobros/${activityId}`
      break
    case TASK_TYPES_KEYS.includes(normalizedActivityName):
      url = `/tareas_en_sitio/${activityId}`
      break
    case normalizedActivityName.includes('liquida'):
      url = `/liquidaciones/detalles/${activityId}`
      break
  }

  return url
}

const getClientKey = (model) => {
  const value = model?.client_uid || model?.client_db_ref || ''
  return String(value).trim()
}

const buildVisitKey = (clientKey, visitDay) => {
  if (!clientKey || !visitDay) return null
  return `${clientKey}__${visitDay}`
}

const getActivityGeofence = (activity) =>
  effectiveLocationStr(activity?.effective_location, activity?.client_latitude, activity?.latitude)

const getTimestampGeofence = (timestamp, locationField = 'start_latitude') =>
  effectiveLocationStr(
    timestamp?.effective_location,
    timestamp?.client_latitude,
    timestamp?.[locationField] || timestamp?.start_latitude || timestamp?.end_latitude
  )

const hasTruthyValue = (value) => value !== null && value !== undefined && value !== ''

const hasCoordinatesPayload = (model, latField, lngField) =>
  hasTruthyValue(model?.[latField]) || hasTruthyValue(model?.[lngField])

const hasCoordinatePair = (model, latField, lngField) =>
  hasTruthyValue(model?.[latField]) && hasTruthyValue(model?.[lngField])

const hasClientMapData = (model) =>
  hasCoordinatePair(model, 'client_latitude', 'client_longitude')

const hasTimestampMapData = (timestamp) =>
  hasCoordinatesPayload(timestamp, 'start_latitude', 'start_longitude') ||
  hasCoordinatesPayload(timestamp, 'end_latitude', 'end_longitude') ||
  hasClientMapData(timestamp)

const hasActivityMapData = (activity) =>
  hasCoordinatesPayload(activity, 'latitude', 'longitude') ||
  hasClientMapData(activity) ||
  activity?.error_coordinates_code != null ||
  activity?.has_allowed_location != null

const getSortTime = (row) => {
  const parsed = row?.datetime ? dayjs(row.datetime).valueOf() : Number.NaN
  return Number.isFinite(parsed) ? parsed : 0
}

const parseDurationToSeconds = (timeString) => {
  if (!timeString || timeString === '-') return null

  const match = String(timeString)
    .trim()
    .match(/^(\d+):([0-5]?\d)(?::([0-5]?\d))?$/)
  if (!match) return null

  const [, firstPart, secondPart, thirdPart] = match

  if (thirdPart === undefined) {
    return Number(firstPart) * 60 + Number(secondPart)
  }

  return Number(firstPart) * 3600 + Number(secondPart) * 60 + Number(thirdPart)
}

const formatDurationFromSeconds = (totalSeconds) => {
  if (!Number.isFinite(totalSeconds)) return '-'

  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0')
  const seconds = String(totalSeconds % 60).padStart(2, '0')

  return `${hours}:${minutes}:${seconds}`
}

const getTimestampVisitDurationSeconds = (timestamp) => {
  return parseDurationToSeconds(timestamp?.visit_duration)
}

export const buildDetailActivityLabel = (activityName, formatActivityName) => {
  const formattedLabel = formatActivityName ? formatActivityName(activityName) : activityName
  const normalizedLabel = String(formattedLabel || activityName || '').trim().toUpperCase()

  const LABEL_MAP = {
    PEDIDO: 'Pedidos',
    COTIZACIÓN: 'Cotización',
    'VISITA NO EFECTIVA': 'Reporte de visita',
    DEVOLUCIÓN: 'Devolución'
  }

  if (!normalizedLabel) return '-'

  return LABEL_MAP[normalizedLabel] || formattedLabel || activityName || '-'
}

const inferDetailType = (detail) => {
  if (
    detail?.type === 'check_in' ||
    detail?.type === 'check_out' ||
    detail?.kind === 'timestamp'
  ) {
    return 'timestamp'
  }

  if (
    hasTruthyValue(detail?.start_visit_time) ||
    hasTruthyValue(detail?.end_visit_time) ||
    hasTruthyValue(detail?.start_latitude) ||
    hasTruthyValue(detail?.end_latitude) ||
    hasTruthyValue(detail?.visited_date)
  ) {
    return 'timestamp'
  }

  return 'activity'
}

const resolveDetailDateTime = (detail) => {
  const directValue =
    detail?.datetime ||
    detail?.date_time ||
    detail?.created_date ||
    detail?.createdAt ||
    detail?.timestamp ||
    detail?.activity_date

  const normalizedDirectValue = normalizeDateTime(directValue)
  if (normalizedDirectValue) {
    return normalizedDirectValue
  }

  const visitDate = detail?.visited_date || detail?.visit_date || detail?.execution_date
  if (detail?.start_visit_time) {
    return buildVisitDateTime(visitDate, detail.start_visit_time)
  }

  if (detail?.end_visit_time) {
    return buildVisitDateTime(visitDate, detail.end_visit_time)
  }

  return null
}

const resolveDetailLabel = (detail, formatActivityName) => {
  if (detail?.label && detail.label !== '-') {
    return detail.label
  }

  if (detail?.type === 'check_in') {
    return 'Check-In'
  }

  if (detail?.type === 'check_out') {
    return 'Check-Out'
  }

  if (detail?.activity_name) {
    return buildDetailActivityLabel(detail.activity_name, formatActivityName)
  }

  return '-'
}

const resolveDetailDescription = (detail, type) => {
  if (type === 'check_in' || type === 'check_out') return '-'

  return detail?.description || detail?.motive_text || detail?.motive || detail?.reason || '-'
}

const resolveDetailAmount = (detail) => {
  if (detail?.amount !== undefined && detail?.amount !== null) {
    return detail.amount
  }

  if (detail?.total !== undefined && detail?.total !== null) {
    return detail.total
  }

  return null
}

const inferMapPayloadKind = (model) =>
  inferDetailType(model) === 'timestamp' ? 'timestamp' : 'activity'

const normalizeExistingMapPayload = (payload) => {
  if (!payload) return null

  if (payload.model) {
    return {
      kind: payload.kind || inferMapPayloadKind(payload.model),
      model: payload.model
    }
  }

  return {
    kind: payload.kind || inferMapPayloadKind(payload),
    model: payload.model || payload
  }
}

const resolveDetailMapPayload = (detail) => {
  const normalizedPayload = normalizeExistingMapPayload(detail?.mapPayload || detail?.map_payload)
  if (normalizedPayload) {
    return normalizedPayload
  }

  const detailType = inferDetailType(detail)

  if (detailType === 'timestamp' && hasTimestampMapData(detail)) {
    return {
      kind: 'timestamp',
      model: detail
    }
  }

  if (hasActivityMapData(detail)) {
    return {
      kind: 'activity',
      model: detail
    }
  }

  return null
}

const resolveDetailInGeofence = (detail) => {
  if (detail?.inGeofence) {
    return detail.inGeofence
  }

  if (detail?.in_geofence) {
    return detail.in_geofence
  }

  if (detail?.in_geofence_label) {
    return detail.in_geofence_label
  }

  if (inferDetailType(detail) === 'timestamp') {
    return getTimestampGeofence(detail)
  }

  return getActivityGeofence(detail)
}

export const normalizeVisitDetailRow = (detail, formatActivityName, fallbackId = 0) => {
  const datetime = resolveDetailDateTime(detail)
  const activityId = [
    detail?.activity_uid,
    detail?.activityCode,
    detail?.activity_code,
    detail?.code
  ].find((value) => hasTruthyValue(value) && value !== '-')
  const activityCode =
    hasTruthyValue(detail?.activityCode) && detail.activityCode !== '-'
      ? detail.activityCode
      : activityId || '-'
  const label = resolveDetailLabel(detail, formatActivityName)
  const type = detail?.type || inferDetailType(detail)
  const activityUrl =
    detail?.activityUrl ||
    detail?.activity_url ||
    buildActivityDetailUrl(detail?.activity_name, activityId)

  return {
    ...detail,
    id: detail?.id || `${type}-${activityCode}-${datetime || fallbackId}`,
    type,
    datetime,
    activityCode,
    label,
    description: resolveDetailDescription(detail, type),
    amount: resolveDetailAmount(detail),
    inGeofence: resolveDetailInGeofence(detail),
    mapPayload: resolveDetailMapPayload(detail),
    activityUrl: type === 'activity' ? activityUrl : ''
  }
}

export const normalizeVisitDetailRows = (details = [], formatActivityName) =>
  (details || []).map((detail, index) => normalizeVisitDetailRow(detail, formatActivityName, index))

export const buildUnifiedVisitLookupKey = (routeRow) => {
  const clientKey = getClientKey(routeRow)
  const visitDay = normalizeDate(routeRow?.execution_date || routeRow?.visit_date)
  return buildVisitKey(clientKey, visitDay)
}

export const buildUnifiedVisitDetails = ({
  activityDownloadData = [],
  timestampDownloadData = [],
  formatActivityName,
  includeTimestampRows = true
}) => {
  const detailsByVisitKey = {}
  const visitSummaryByKey = {}

  const ensureGroup = (key) => {
    if (!key) return null

    if (!detailsByVisitKey[key]) {
      detailsByVisitKey[key] = []
    }

    if (!visitSummaryByKey[key]) {
      visitSummaryByKey[key] = {
        timestampEntry: null,
        firstActivityEntry: null,
        firstGeofenceActivityEntry: null,
        locationMapPayload: null,
        inGeofence: 'N/A',
        visitDuration: '-',
        totalVisitDurationSeconds: 0
      }
    }

    return visitSummaryByKey[key]
  }

  activityDownloadData.forEach((activity) => {
    const key = buildVisitKey(getClientKey(activity), normalizeDate(activity?.created_date))
    const summary = ensureGroup(key)
    if (!summary) return

    if (!summary.firstActivityEntry) {
      summary.firstActivityEntry = activity
    }

    if (!summary.firstGeofenceActivityEntry && getActivityGeofence(activity) !== 'N/A') {
      summary.firstGeofenceActivityEntry = activity
    }

    if (!summary.locationMapPayload && hasActivityMapData(activity)) {
      summary.locationMapPayload = {
        kind: 'activity',
        model: activity
      }
    }

    if (summary.inGeofence === 'N/A') {
      summary.inGeofence = getActivityGeofence(activity)
    }

    detailsByVisitKey[key].push({
      id: `activity-${activity.activity_uid || activity.created_date || detailsByVisitKey[key].length}`,
      type: 'activity',
      datetime: normalizeDateTime(activity?.created_date),
      activityCode: activity?.activity_uid || '-',
      label: buildDetailActivityLabel(activity?.activity_name, formatActivityName),
      description: activity?.motive_text || '-',
      amount: activity?.total ?? null,
      inGeofence: getActivityGeofence(activity),
      activityUrl: buildActivityDetailUrl(activity?.activity_name, activity?.activity_uid),
      mapPayload: hasActivityMapData(activity)
        ? {
          kind: 'activity',
          model: activity
        }
        : null
    })
  })

  timestampDownloadData.forEach((timestamp) => {
    const key = buildVisitKey(getClientKey(timestamp), normalizeDate(timestamp?.visited_date))
    const summary = ensureGroup(key)
    if (!summary) return

    const durationSeconds = getTimestampVisitDurationSeconds(timestamp)
    if (durationSeconds !== null) {
      summary.totalVisitDurationSeconds += durationSeconds
      summary.visitDuration = formatDurationFromSeconds(summary.totalVisitDurationSeconds)
    }

    if (!summary.timestampEntry) {
      summary.timestampEntry = timestamp
      if (hasTimestampMapData(timestamp)) {
        summary.locationMapPayload = {
          kind: 'timestamp',
          model: timestamp
        }
      }
      summary.inGeofence = getTimestampGeofence(timestamp)
    }

    if (!includeTimestampRows) return

    if (timestamp?.start_visit_time) {
      detailsByVisitKey[key].push({
        id: `check-in-${timestamp.client_db_ref || timestamp.client_name}-${timestamp.visited_date}-${timestamp.start_visit_time}`,
        type: 'check_in',
        datetime: buildVisitDateTime(timestamp?.visited_date, timestamp?.start_visit_time),
        activityCode: '-',
        label: 'Check-In',
        description: '-',
        amount: null,
        inGeofence: getTimestampGeofence(timestamp, 'start_latitude'),
        activityUrl: '',
        mapPayload: hasTimestampMapData(timestamp)
          ? {
            kind: 'timestamp',
            model: timestamp
          }
          : null
      })
    }

    if (timestamp?.status === 'completed' && timestamp?.end_visit_time) {
      detailsByVisitKey[key].push({
        id: `check-out-${timestamp.client_db_ref || timestamp.client_name}-${timestamp.visited_date}-${timestamp.end_visit_time}`,
        type: 'check_out',
        datetime: buildVisitDateTime(timestamp?.visited_date, timestamp?.end_visit_time),
        activityCode: '-',
        label: 'Check-Out',
        description: '-',
        amount: null,
        inGeofence: getTimestampGeofence(timestamp, 'end_latitude'),
        activityUrl: '',
        mapPayload: hasTimestampMapData(timestamp)
          ? {
            kind: 'timestamp',
            model: timestamp
          }
          : null
      })
    }
  })

  Object.keys(detailsByVisitKey).forEach((key) => {
    const summary = visitSummaryByKey[key]

    if (
      summary?.timestampEntry &&
      summary.inGeofence === 'N/A' &&
      summary.firstGeofenceActivityEntry
    ) {
      summary.inGeofence = getActivityGeofence(summary.firstGeofenceActivityEntry)
    }

    if (!summary?.locationMapPayload && summary?.firstActivityEntry) {
      summary.locationMapPayload = hasActivityMapData(summary.firstActivityEntry)
        ? {
          kind: 'activity',
          model: summary.firstActivityEntry
        }
        : null
    }

    detailsByVisitKey[key].sort((left, right) => {
      const byTime = getSortTime(right) - getSortTime(left)
      if (byTime !== 0) return byTime

      const leftPriority = DETAIL_PRIORITY[left.type] ?? 99
      const rightPriority = DETAIL_PRIORITY[right.type] ?? 99
      return leftPriority - rightPriority
    })
  })

  return {
    detailsByVisitKey,
    visitSummaryByKey
  }
}
